import { Account } from "@rentablo/api/apiv1";
import { AccountTypeFromBankEnum } from "@rentablo/api/apiv1";

export interface Option {
  id: number;
  accountName?: string;
  iban?: string;
  bankName?: string;
  bankImage?: string;
  type?: AccountTypeFromBankEnum;
}

const toOptions = (accounts: Account[]): Option[] =>
  accounts
    .filter((account) => !!account.externalId)
    .map((account) => ({
      id: account.id,
      accountName: account.name,
      iban: account.iban,
      bankName: account.bankName,
      bankImage: account.iconUrl,
      type: account.typeFromBank,
    }))
    .sort((a, b) =>
      `${a.bankName}${a.accountName}`.localeCompare(
        `${b.bankName}${b.accountName}`
      )
    );

export interface DerivedContraAccounts {
  primary?: Account;
  additional: Account[];
  candidates: Account[];
  options: Option[];
}

export const deriveContraAccounts = (
  accounts: Account[],
  depot: Account
): DerivedContraAccounts => {
  const findById = (id?: number) =>
    accounts.find((account) => account.id === id);
  const additionalIds = (depot.additionalContraAccountIds ?? []).filter(
    (id) => id !== depot.contraAccountId
  );
  const linkedIds = new Set(
    [depot.contraAccountId, ...additionalIds].filter(Boolean)
  );
  const primary = findById(depot.contraAccountId);
  const additional = additionalIds
    .map(findById)
    .filter((account): account is Account => Boolean(account));
  // Only group candidates by bank connection when the depot actually has one.
  // A manual depot has no bankConnectionId, and matching `undefined === undefined`
  // would wrongly pull in every unconnected cash account.
  const candidates = depot.bankConnectionId
    ? accounts.filter(
        (account) =>
          account.type === "02_cash" &&
          !linkedIds.has(account.id) &&
          account.bankConnectionId === depot.bankConnectionId
      )
    : [];

  return { primary, additional, candidates, options: toOptions(candidates) };
};
